import { ClientLaunchStatus, ClientLaunchType } from '../../../graphql/types';
import type {
  AutoCheckContext,
  CheckpointItem,
  ClientLaunch,
  DiagnosticsResult,
  DiagnosticsState,
  LaunchStep,
  StepStatus,
} from './types';

export const REVIEW_STEP_ID = 'review';

export const isReadOnly = (launch: ClientLaunch) =>
  launch.status !== ClientLaunchStatus.InProgress;

export const activeItemIds = (launch: ClientLaunch, stepId: string) =>
  new Set(
    launch.confirmations
      .filter((c) => c.stepId === stepId && !c.clearedAt)
      .map((c) => c.itemId),
  );

/**
 * The launch without the confirmations of steps edited since, so they stop
 * counting before the backend has cleared them, or when clearing failed.
 */
export const withoutConfirmations = (
  launch: ClientLaunch,
  stepIds: ReadonlySet<string>,
): ClientLaunch =>
  stepIds.size
    ? {
        ...launch,
        confirmations: launch.confirmations.filter(
          (c) => !stepIds.has(c.stepId),
        ),
      }
    : launch;

export const isItemDone = (
  item: CheckpointItem,
  confirmed: Set<string>,
  context: AutoCheckContext,
) => (item.isAutoDone ? item.isAutoDone(context) : confirmed.has(item.id));

export const getStepStatuses = (
  steps: LaunchStep[],
  launch: ClientLaunch,
  dirtySteps: ReadonlySet<string>,
  context: AutoCheckContext,
): StepStatus[] => {
  let earlierComplete = true;
  return steps.map((step) => {
    const items = step.checkpoint(launch);
    const confirmed = activeItemIds(launch, step.id);
    const confirmedCount = items.filter((item) =>
      isItemDone(item, confirmed, context),
    ).length;
    const saved = launch.savedSteps.includes(step.id);
    const dirty = dirtySteps.has(step.id);
    const checkpointDone = confirmedCount === items.length;
    const status: StepStatus = {
      id: step.id,
      saved,
      dirty,
      confirmedCount,
      itemCount: items.length,
      checkpointDone,
      complete: saved && !dirty && checkpointDone,
      reachable: earlierComplete,
    };
    earlierComplete = earlierComplete && status.complete;
    return status;
  });
};

/** The saved step to resume at, or the furthest one that can be reached. */
export const getInitialStepIndex = (
  steps: LaunchStep[],
  statuses: StepStatus[],
  launch: ClientLaunch,
) => {
  if (isReadOnly(launch)) {
    return steps.length - 1;
  }
  const saved = steps.findIndex((step) => step.id === launch.currentStep);
  if (saved >= 0 && statuses[saved].reachable) {
    return saved;
  }
  return statuses.filter((status) => status.reachable).length - 1;
};

export const getContinueLabel = (
  step: LaunchStep,
  status: StepStatus,
  type: ClientLaunchType,
) => {
  if (step.id === REVIEW_STEP_ID) {
    return type === ClientLaunchType.FeatureLaunch
      ? 'Finish launch'
      : 'Finish setup';
  }
  return !status.saved || status.dirty ? 'Save & continue' : 'Continue';
};

const plural = (count: number, word: string) =>
  `${count} ${word}${count === 1 ? '' : 's'}`;

/** Why continue is disabled, or undefined when it isn't. */
export const getBlockedReason = (stepIndex: number, statuses: StepStatus[]) => {
  const status = statuses[stepIndex];
  const isLast = stepIndex === statuses.length - 1;
  if (isLast && statuses.slice(0, -1).some((s) => !s.complete)) {
    return 'Save and confirm every step before finishing.';
  }
  const remaining = status.itemCount - status.confirmedCount;
  if (remaining > 0) {
    return `Confirm ${plural(remaining, 'more item')} to continue.`;
  }
  return undefined;
};

export const getDiagnosticsState = (
  result: DiagnosticsResult | undefined,
  editVersion: number,
): DiagnosticsState => {
  if (!result) {
    return { kind: 'notRun' };
  }
  if (result.editVersion !== editVersion) {
    return { kind: 'stale' };
  }
  return { kind: 'done', issues: result.issues };
};

/** Default launch name from its features, e.g. "Service Booking and 2 more". */
export const autoLaunchName = (features: ClientLaunch['features']) => {
  const names = features.map((f) => f.name);
  return names.length > 2
    ? `${names[0]} and ${names.length - 1} more`
    : names.join(' and ');
};

export const getLaunchTitle = (launch: ClientLaunch) =>
  launch.type === ClientLaunchType.FeatureLaunch
    ? launch.name || autoLaunchName(launch.features) || 'Feature launch'
    : (launch.client.name ?? 'New client');
